import { HisUploadFile } from '../../../model/his-upload-file';
import { EudiwMedia } from './eudiw-media.model';
import { LocalMedia } from './local-media.model';
import { LocalMediaSelection } from './local-media-selection.model';
import { UploadSourceType } from './upload-source-type.model';

/**
 * A single selected file of {@code <his-select-file />}, either an upload or a reference
 * to a file that is already stored for the person ("local media").
 *
 * The optional properties are declared as {@code | null} on purpose: the component clears
 * them by writing {@code null} rather than by deleting them, because only {@code null}
 * survives the JSON serialization towards the server. An absent property would simply be
 * left out of the request and thus keep whatever the server still has stored.
 */
export interface HisSelectFile {

    /** Where the file comes from. */
    sourceType: UploadSourceType;

    /** The comment the user entered for this file; {@code null} while there is none. */
    comment?: string | null;

    /** The document-metadata category of this file; {@code null} while none was determined. */
    documentMetadataCategoryId?: number | null;

    /** Indicators associated with this file. */
    indicators?: string[];

    /** The uploaded file, set for {@link UploadSourceType.UPLOAD}. */
    upload?: HisUploadFile;

    /** The referenced stored file, set for {@link UploadSourceType.LOCAL_MEDIA}. */
    localMedia?: LocalMediaSelection;

    /** The temporary EUDI-Wallet resource, set for {@link UploadSourceType.EUDIW}. */
    eudiwMedia?: EudiwMedia;
}

/**
 * The name the given file is presented with: the name of the upload, the name of the local
 * media it refers to — or, while those are not (yet) loaded, its media reference. `null` when
 * none of them can be determined.
 *
 * Shared by the parts of {@code <his-select-file />} that present a file: its name is rendered
 * in the {@code <dt>} of the definition list, while the actions in the neighbouring
 * {@code <dd>} embed the same name in their titles.
 */
export function hisSelectFileName( file: HisSelectFile, localMedia?: LocalMedia ): string | null {
    if ( file?.upload ) {
        return file.upload.name;
    }

    if ( file?.eudiwMedia ) {
        return file.eudiwMedia.name || file.eudiwMedia.mediaReference;
    }

    return localMedia?.name ?? file?.localMedia?.mediaReference ?? null;
}

/**
 * Surrogate keys handed out to files that carry no server-side reference, kept in a
 * {@link WeakMap} so they disappear together with the file they belong to.
 */
const surrogateKeys = new WeakMap<HisSelectFile, string>();
let surrogateKeySequence = 0;

/**
 * Returns a stable identity key for the given file, used for change tracking
 * (e.g. Angular {@code @for} tracking and per-file caching).
 *
 * Uploads are keyed by their {@code uuid} (falling back to {@code id}); local-media
 * selections by their {@code mediaReference}.
 *
 * A file that carries neither reference - e.g. an upload the server has not
 * acknowledged yet - gets a surrogate key instead. Returning a constant key for those
 * would make Angular's {@code @for} see duplicates and throw as soon as a second such
 * file is selected. The surrogate key is tied to the object identity, so a file that is
 * copied (as the component does when it assigns the common category) receives a new key
 * and is re-rendered; that is the price for keeping distinct files distinguishable.
 */
export function hisSelectFileKey( file: HisSelectFile ): string {
    if ( file == null ) {
        return '';
    }

    if ( file.upload ) {
        const reference = file.upload.uuid ?? file.upload.id;
        if ( reference != null ) {
            return `upload:${ reference }`;
        }
    }

    if ( file.localMedia?.mediaReference != null ) {
        return `localMedia:${ file.localMedia.mediaReference }`;
    }

    if ( file.eudiwMedia?.mediaReference != null ) {
        return `eudiw:${ file.eudiwMedia.mediaReference }`;
    }

    let surrogate = surrogateKeys.get( file );
    if ( surrogate == null ) {
        surrogate = `surrogate:${ ++surrogateKeySequence }`;
        surrogateKeys.set( file, surrogate );
    }

    return surrogate;
}
